/**
 *  @class
 *  @function SlideShow
 *
 *  Salesforce Data Cloud (360) measurement traces (uni2 vocabulary):
 *    - slideshow_decision        (every personalisation evaluation, per slide)
 *    - slideshow_view            (slide impression while visible AND active)
 *    - slideshow_click           (anchor / button click inside a personalised slide)
 *    - slideshow_control         (posture = control)
 *    - slideshow_suppressed      (posture = suppressed)
 *    - slideshow_hidden_no_match (posture = no_match / hidden / no payload returned)
 *
 *  Final payload shape must be confirmed with Performance Lead before go-live.
 */
const slideshowIsShiels = () => window.STORE_CONTEXT?.isShiels === true;

const slideshowEmitTrace = (eventType, context = {}) => {
  if (!slideshowIsShiels()) return;
  if (
    typeof window === "undefined" ||
    !window.SalesforceInteractions ||
    typeof window.SalesforceInteractions.sendEvent !== "function"
  ) {
    return;
  }
  const {
    personalisationId,
    contentId,
    decisionId,
    personalizationPointId,
    personalizationPointName,
    posture,
    attributes,
    source,
    slideIndex,
    slideId,
    href,
  } = context;
  try {
    window.SalesforceInteractions.sendEvent({
      interaction: {
        name: `slideshow_${eventType}`,
        eventType: "userEngagement",
        engagementType: eventType,
        source: source || "slideshow",
        personalization: contentId ? { id: contentId, contentId: contentId } : undefined,
        personalizationPointName,
        personalizationPointId,
        contentId,
        decisionId,
        posture,
        slideIndex,
        slideId,
        href,
        attributes: attributes || {},
      },
    });
    console.log("[slideshow] Trace", eventType, context);
  } catch (e) {
    console.warn("[slideshow] Trace failed", eventType, e);
  }
};

const slideshowResolvePosture = (attributes) => {
  if (!attributes) return "active";
  const raw =
    attributes.Posture ||
    attributes.posture ||
    attributes.Variant ||
    attributes.Decision;
  if (!raw) return "active";
  const v = String(raw).toLowerCase().replace(/[\s\-]+/g, "_");
  if (v === "active" || v === "variant" || v === "show") return "active";
  if (v === "control" || v === "holdout") return "control";
  if (v === "suppressed" || v === "suppress") return "suppressed";
  if (v === "no_match" || v === "nomatch" || v === "hidden" || v === "hide") {
    return "no_match";
  }
  return "active";
};

if (!customElements.get("slide-show")) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      const slideshow = this;

      // iOS specific fixes
      this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (this.isIOS) {
        // Force hardware acceleration for iOS
        slideshow.style.webkitTransform = 'translateZ(0)';
        slideshow.style.transform = 'translateZ(0)';
        slideshow.style.webkitBackfaceVisibility = 'hidden';
        slideshow.style.backfaceVisibility = 'hidden';
      }

      let parent_section = slideshow.closest(".shopify-section"),
        is_collection_tabs = slideshow.dataset.isCollectionTabs,

        slideshow_slides = Array.from(slideshow.querySelectorAll(".carousel__slide")),
        autoplay = slideshow.dataset.autoplay == "false" ? false : parseInt(slideshow.dataset.autoplay, 10),
        align = slideshow.dataset.align == "center" ? "center" : "left",
        fade = slideshow.dataset.fade == "true" ? true : false,
        pageDots =
          slideshow.dataset.dots == "true" && is_collection_tabs && window.innerWidth <= 500
            ? true
            : slideshow.dataset.dots == "true" && is_collection_tabs
            ? false
            : slideshow.dataset.dots == "true"
            ? true
            : false,
        prev_button = slideshow.querySelector(".flickity-prev"),
        next_button = slideshow.querySelector(".flickity-next"),
        mobile_prev_button = slideshow.querySelector("#drawer-cart-prev"),
        mobile_next_button = slideshow.querySelector("#drawer-cart-next"),
        isMobile = window.innerWidth <= 768,
        header = document.querySelector("theme-header"),
        custom_dots = slideshow.querySelector(".flickity-custom-dots"),
        custom_navigation = slideshow.querySelector(".flickity-custom-navigation"),
        autoplay_progress = slideshow.querySelector(".slideshow--autoplay-progress"),
        progress_bar = slideshow.parentNode.querySelector(".flickity-progress--bar"),
        animations = [],
        rightToLeft = document.dir === "rtl",
        animations_enabled = document.body.classList.contains("animations-true") && typeof gsap !== "undefined",
        selectedIndex = 0,
        tick = autoplay ? window.setInterval(autoplay, 10) : false,
        args = {
          wrapAround: true,
          cellAlign: align,
          pageDots: pageDots,
          contain: true,
          fade: fade,
          autoPlay: autoplay,
          rightToLeft: rightToLeft,
          prevNextButtons: false,
          cellSelector: ".carousel__slide",
          on: {},
        };

        // iOS specific configuration
        if (this.isIOS) {
          args.draggable = true;
          args.freeScroll = false;
          args.accessibility = true;
          args.imagesLoaded = true;
          // Allow vertical scroll by not preventing default touch behavior
          args.dragThreshold = 10;
        }

      this.paused = false;
      if (custom_dots) {
        args.pageDots = false;
      }

      if (is_collection_tabs) {
        args.adaptiveHeight = false;

        if (window.innerWidth <= 500) {
          args.pageDots = true;
          args.groupCells = 2;
        }

        // Allow vertical scroll for collection tabs
        if (this.isIOS) {
          args.dragThreshold = 15;
        }
      }

      if (mobile_prev_button && mobile_next_button) {
        if (isMobile) {
          mobile_prev_button.style.opacity = 1;
          mobile_prev_button.style.display = "flex";
          mobile_next_button.style.opacity = 1;
          mobile_next_button.style.display = "flex";
        }
      }
      if (slideshow.classList.contains("image-with-text-slideshow--content")) {
        let main_slideshow = slideshow.parentNode.querySelector(".image-with-text-slideshow--image");
        args.draggable = false;
        args.asNavFor = main_slideshow;
      }
      if (slideshow.classList.contains("image-with-text-slideshow--image")) {
        let image_slideshow_slides = slideshow.querySelectorAll(".image-with-text-slideshow--image-media");

        if (image_slideshow_slides.length) {
          if (image_slideshow_slides[0].classList.contains("desktop-height-auto")) {
            args.adaptiveHeight = true;
          }
        }
      }
      if (
        slideshow.classList.contains("image-with-text-slideshow--content") ||
        slideshow.classList.contains("customer-reviews--content") ||
        slideshow.classList.contains("customer-reviews--image")
      ) {
        args.adaptiveHeight = true;
      }
      if (slideshow.classList.contains("customer-reviews--carousel")) {
        args.wrapAround = false;
        args.adaptiveHeight = false;
        args.resize = true;
        args.on.ready = function () {
          let flkty = this;
          flkty.resize();
          document.fonts.ready.then(function () {
            flkty.resize();
          });
        };
      }
      if (slideshow.classList.contains("collection-grid__carousel")) {
        args.wrapAround = false;
        // Allow vertical scroll for collection grid carousel
        if (this.isIOS) {
          args.dragThreshold = 20;
        }
      }
      // Main Slideshow
      if (slideshow.classList.contains("main-slideshow")) {
        if (animations_enabled) {
          slideshow.prepareAnimations(slideshow, animations);
        }

        args.on = {
          staticClick: function () {
            this.unpausePlayer();
          },
          ready: function () {
            let flkty = this;

            // Transparent Header padding.
            if (header) {
              if (header.classList.contains("transparent--true")) {
                let sections = Array.from(document.querySelector("#main-content").querySelectorAll(":scope > .shopify-section")),
                  i = sections.indexOf(parent_section);

                if (i == 0 && slideshow.classList.contains("section-spacing--disable-top")) {
                  slideshow.classList.add("slideshow--top");
                }
              }
            }
            // Animations.
            if (animations_enabled) {
              slideshow.animateSlides(0, slideshow, animations);
            }

            // AutoPlay Progress
            if (autoplay && autoplay_progress) {
              slideshow.setupAutoplayProgress(slideshow, flkty);
            }

            // Fonts resize
            document.fonts.ready.then(function () {
              flkty.resize();
            });

            // Video Support.
            let video_container = flkty.cells[0].element.querySelector(".slideshow__slide-video-bg");
            if (video_container) {
              if (video_container.querySelector("iframe")) {
                video_container.querySelector("iframe").onload = function () {
                  slideshow.videoPlay(video_container);
                };
              } else if (video_container.querySelector("video")) {
                video_container.querySelector("video").onloadstart = function () {
                  slideshow.videoPlay(video_container);
                };
              }
            }
          },
          change: function (index) {
            let flkty = this,
              previousIndex = fizzyUIUtils.modulo(flkty.selectedIndex - 1, flkty.slides.length);

            // Animations.
            if (animations_enabled) {
              slideshow.animateReverse(previousIndex, slideshow, animations);
              slideshow.animateSlides(index, slideshow, animations);
            }

            // Color changes.
            let color_text = getComputedStyle(this.selectedElement).getPropertyValue("--color-text");
            if (autoplay_progress && animations_enabled) {
              autoplay_progress.style.setProperty("--color-body", color_text);
            }
            let dots = slideshow.querySelector(".flickity-page-dots");
            if (dots) {
              dots.style.setProperty("--color-body", color_text);
            }

            // Custom Dots.
            if (custom_navigation) {
              custom_navigation.querySelector(".flickity-custom-navigation--current").innerHTML = this.selectedIndex + 1;
            }

            // AutoPlay Progress
            if (autoplay) {
              if (flkty.player.state !== "paused") {
                flkty.stopPlayer();
                flkty.playPlayer();
              }
            }
            if (autoplay && autoplay_progress && animations_enabled) {
              slideshow.autoPlayProgressTL.progress(0);

              if (flkty.player.state !== "paused") {
                slideshow.autoPlayProgressTL.play();
              }
            }

            // Video Support.
            // previous slide
            let video_container_prev = flkty.cells[previousIndex].element.querySelector(".slideshow__slide-video-bg");
            if (video_container_prev) {
              slideshow.videoPause(video_container_prev);
            }
            // current slide
            let video_container = flkty.cells[index].element.querySelector(".slideshow__slide-video-bg");
            if (video_container) {
              if (video_container.querySelector("iframe")) {
                if (video_container.querySelector("iframe").classList.contains("lazyload")) {
                  video_container.querySelector("iframe").addEventListener("lazybeforeunveil", slideshow.videoPlay(video_container));
                  lazySizes.loader.checkElems();
                } else {
                  slideshow.videoPlay(video_container);
                }
              } else if (video_container.querySelector("video")) {
                slideshow.videoPlay(video_container);
              }
            }
          },
        };
        if (slideshow.classList.contains("desktop-height-image") || slideshow.classList.contains("mobile-height-image")) {
          args.adaptiveHeight = true;
        }
      }

      // Media with tabs
      if (slideshow.classList.contains("media-with-tabs--media")) {
        args.draggable = false;
        args.on = {
          staticClick: function () {
            this.unpausePlayer();
          },
          ready: function () {
            let flkty = this;

            // Video Support.
            let video_container = flkty.cells[0].element.querySelector(".media-with-tabs--media--video");
            if (video_container) {
              video_container.querySelector("video").onloadstart = function () {
                slideshow.videoPlay(video_container);
              };
            }
          },
          change: function (index) {
            let flkty = this,
              previousIndex = fizzyUIUtils.modulo(flkty.selectedIndex - 1, flkty.slides.length);

            // Video Support.
            // previous slide
            let video_container_prev = flkty.cells[previousIndex].element.querySelector(".media-with-tabs--media--video");
            if (video_container_prev) {
              slideshow.videoPause(video_container_prev);
            }
            // current slide
            let video_container = flkty.cells[index].element.querySelector(".media-with-tabs--media--video");
            if (video_container) {
              slideshow.videoPlay(video_container);
            }
          },
        };
      }

      // Announcement bar color change
      if (slideshow.classList.contains("announcement-bar--use-colors-true")) {
        args.on.change = function (index) {
          const container = slideshow.closest(".announcement-bar");
          let color_text = this.selectedElement.dataset.color,
            color_bg = this.selectedElement.dataset.bg;

          container.style.setProperty("--color-announcement-bar-bg", color_bg);
          container.style.setProperty("--color-announcement-bar-text", color_text);
        };
      }

      // Product Carousels
      if (slideshow.classList.contains("products")) {
        args.wrapAround = false;
        args.on.ready = function () {
          var flickity = this;
          if (next_button) {
            window.addEventListener("resize.center_arrows", function () {
              slideshow.centerArrows(flickity, slideshow, prev_button, next_button);
            });
          }
          window.dispatchEvent(new Event("resize.center_arrows"));
        };
      }
      if (progress_bar) {
        args.wrapAround = false;
        args.on.scroll = function (progress) {
          progress = Math.max(0, Math.min(1, progress));
          progress_bar.style.width = progress * 100 + "%";
        };
      }

      // Testimonials
      if (custom_dots) {
        args.on = {
          ready: function () {
            let flkty = this;

            // Custom Dots.
            let dots = custom_dots.querySelectorAll("li");
            dots.forEach((dot, i) => {
              dot.addEventListener("click", (e) => {
                flkty.select(i);
              });
            });
            dots[this.selectedIndex].classList.add("is-selected");
          },
          change: function (index) {
            // Custom Dots.
            let dots = custom_dots.querySelectorAll("li");
            dots.forEach((dot, i) => {
              dot.classList.remove("is-selected");
            });
            dots[this.selectedIndex].classList.add("is-selected");
          },
        };
      }

      // Customer reviews
      if (slideshow.classList.contains("customer-reviews--carousel")) {
        args.on.ready = function () {
          var flickity = this;
          if (next_button) {
            window.addEventListener("resize.center_arrows", function () {
              slideshow.centerArrows(flickity, slideshow, prev_button, next_button);
            });
          }
          window.dispatchEvent(new Event("resize.center_arrows"));
        };
      }

      // Initiate
      const flkty = new Flickity(slideshow, args);

      selectedIndex = flkty.selectedIndex;

      slideshow.dataset.initiated = true;

      // Ensure promotion columns maintain proper aspect ratio on load for collection tabs
      if (is_collection_tabs) {
        // Function to recalculate heights after images load
        const recalculatePromotionHeights = function() {
          const promotionColumns = slideshow.querySelectorAll('.promotion-column .gallery--item figure.aspect-ratio');
          if (promotionColumns.length > 0) {
            let imagesLoaded = 0;
            const totalImages = promotionColumns.length;

            promotionColumns.forEach((figure) => {
              const img = figure.querySelector('img');
              if (img) {
                if (img.complete) {
                  imagesLoaded++;
                } else {
                  img.addEventListener('load', function() {
                    imagesLoaded++;
                    if (imagesLoaded === totalImages) {
                      // All images loaded, recalculate Flickity
                      setTimeout(() => {
                        flkty.reloadCells();
                        flkty.resize();
                      }, 50);
                    }
                  }, { once: true });
                }
              } else {
                // No image, count as loaded
                imagesLoaded++;
              }
            });

            // If all images are already loaded
            if (imagesLoaded === totalImages) {
              setTimeout(() => {
                flkty.reloadCells();
                flkty.resize();
              }, 50);
            }
          } else {
            // No promotion columns, but still ensure proper sizing
            setTimeout(() => {
              flkty.reloadCells();
              flkty.resize();
            }, 100);
          }
        };

        // Run on initial load
        setTimeout(recalculatePromotionHeights, 100);

        // Also run when Flickity is ready
        flkty.on('ready', function() {
          setTimeout(recalculatePromotionHeights, 150);
        });

        // Recalculate on window resize (important for mobile orientation changes)
        let resizeTimeout;
        window.addEventListener('resize', function() {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(function() {
            recalculatePromotionHeights();
          }, 250);
        });
      }

      // iOS specific fixes after initialization
      if (this.isIOS) {
        // Forces browser reflow for rendering
        slideshow.offsetHeight;

        // Add touch event listeners for better iOS support
        const viewport = slideshow.querySelector('.flickity-viewport');
        if (viewport) {
          viewport.style.webkitOverflowScrolling = 'touch';
          viewport.style.touchAction = 'auto';
          viewport.style.overscrollBehaviorX = 'contain';
        }

        // Force hardware acceleration on slides
        const slider = slideshow.querySelector('.flickity-slider');
        if (slider) {
          slider.style.touchAction = 'auto';
        }

        // Add iOS-specific event listeners that allow vertical scroll
        let startY = 0;
        let startX = 0;

        slideshow.addEventListener('touchstart', function(e) {
          if (e.touches.length === 1) {
            startY = e.touches[0].clientY;
            startX = e.touches[0].clientX;
          }
        }, { passive: true });

        slideshow.addEventListener('touchmove', function(e) {
          if (e.touches.length === 1) {
            const currentY = e.touches[0].clientY;
            const currentX = e.touches[0].clientX;
            const deltaY = Math.abs(currentY - startY);
            const deltaX = Math.abs(currentX - startX);

            // If vertical movement is greater than horizontal, allow page scroll
            if (deltaY > deltaX) {
              e.stopPropagation();
              return;
            }
          }
        }, { passive: true });
      }

      // Arrows
      if (prev_button) {
        prev_button.addEventListener("click", (event) => {
          flkty.previous();
        });
        prev_button.addEventListener("keyup", (event) => {
          flkty.previous();
        });
        next_button.addEventListener("click", (event) => {
          flkty.next();
        });
        next_button.addEventListener("keyup", (event) => {
          flkty.next();
        });
      }

      // Theme editor
      if (Shopify.designMode) {
        slideshow.addEventListener("shopify:block:select", (event) => {
          let index = slideshow_slides.indexOf(event.target);
          flkty.select(index);
        });
      }

      // Salesforce Data Cloud (360) per-slide personalisation lookup (Shiels only)
      if (slideshowIsShiels()) {
        this.fetchPersonalisationsForSlides();
      }
    }
    /**
     * Collects every slide block that has a `data-personalisation-id` attribute
     * (set via the per-slide Personalisation ID block setting), batches them
     * into a single `SalesforceInteractions.Personalization.fetch(...)` call,
     * then writes the resolved IDs back onto each slide individually.
     */
    fetchPersonalisationsForSlides() {
      if (!slideshowIsShiels()) return;
      const slideshow = this;
      const slides = Array.from(slideshow.querySelectorAll(".carousel__slide[data-personalisation-id]")).filter(
        (slide) => !slide.closest(".announcement-bar")
      );
      if (slides.length === 0) return;

      const slidesByPointName = new Map();
      slides.forEach((slide) => {
        const id = slide.dataset.personalisationId;
        if (!id) return;
        if (!slidesByPointName.has(id)) slidesByPointName.set(id, []);
        slidesByPointName.get(id).push(slide);
      });
      const pointNames = Array.from(slidesByPointName.keys());
      if (pointNames.length === 0) return;

      const clearSkeleton = (slide) => {
        if (!slide || !slide.classList.contains("slideshow__slide--personalising")) return;
        slide.classList.remove("slideshow__slide--personalising");
        const skeleton = slide.querySelector(".slideshow__slide-skeleton");
        if (skeleton) {
          setTimeout(() => skeleton.remove(), 400);
        }
      };

      const SKELETON_TIMEOUT_MS = 6000;
      const skeletonTimeout = setTimeout(() => {
        slides.forEach(clearSkeleton);
      }, SKELETON_TIMEOUT_MS);

      const MAX_WAIT_MS = 15000;
      const POLL_INTERVAL_MS = 100;
      const start = Date.now();

      const sdkReady = () =>
        typeof window !== "undefined" &&
        window.SalesforceInteractions &&
        window.SalesforceInteractions.Personalization &&
        typeof window.SalesforceInteractions.Personalization.fetch === "function";

      const resolveSdk = () =>
        new Promise((resolve, reject) => {
          if (sdkReady()) return resolve(window.SalesforceInteractions);
          const interval = setInterval(() => {
            if (sdkReady()) {
              clearInterval(interval);
              resolve(window.SalesforceInteractions);
            } else if (Date.now() - start > MAX_WAIT_MS) {
              clearInterval(interval);
              reject(new Error("Salesforce Data Cloud SDK / Personalization.fetch not available"));
            }
          }, POLL_INTERVAL_MS);
        });

      const applyToSlide = (slide, personalization) => {
        const attributes = (personalization && personalization.attributes) || {};
        const contentId =
          (personalization && personalization.personalizationId) ||
          attributes.contentId ||
          attributes.ContentId ||
          attributes.content_id ||
          null;
        const decisionId = personalization && personalization.decisionId;
        const personalizationPointId = personalization && personalization.personalizationPointId;
        const personalizationPointName = personalization && personalization.personalizationPointName;
        const slideIndex = Array.from(slideshow.querySelectorAll(".carousel__slide")).indexOf(slide);
        const posture = slideshowResolvePosture(attributes);

        console.log("[slideshow] Slide personalisation result", {
          slideIndex,
          personalisationId: slide.dataset.personalisationId,
          contentId,
          decisionId,
          personalizationPointId,
          personalizationPointName,
          posture,
          attributes,
          personalization,
        });

        if (contentId) slide.dataset.personalisationContentId = contentId;
        if (decisionId) slide.dataset.personalisationDecisionId = decisionId;
        if (personalizationPointId) slide.dataset.personalisationPointId = personalizationPointId;
        if (personalizationPointName) slide.dataset.personalisationPointName = personalizationPointName;
        slide.dataset.personalisationPosture = posture;

        const traceContext = {
          personalisationId: slide.dataset.personalisationId,
          contentId,
          decisionId,
          personalizationPointId,
          personalizationPointName,
          posture,
          attributes,
          slideIndex,
          slideId: slide.id || slide.dataset.blockId || undefined,
        };
        slide._slideshowTraceContext = traceContext;

        slideshowEmitTrace("decision", traceContext);

        if (posture === "control") {
          slideshowEmitTrace("control", traceContext);
          clearSkeleton(slide);
        } else if (posture === "suppressed") {
          slideshowEmitTrace("suppressed", traceContext);
          slide.classList.add("slideshow__slide--suppressed");
          clearSkeleton(slide);
        } else if (posture === "no_match") {
          slideshowEmitTrace("hidden_no_match", traceContext);
          clearSkeleton(slide);
        } else {
          slideshow
            .applyPersonalisationContent(slide, attributes)
            .then(() => {
              clearSkeleton(slide);
              slideshow.armSlideViewTrace(slide);
              slideshow.armSlideClickTrace(slide);
            })
            .catch((e) => {
              console.warn("[slideshow] applyPersonalisationContent failed", e);
              clearSkeleton(slide);
              slideshow.armSlideViewTrace(slide);
              slideshow.armSlideClickTrace(slide);
            });
        }

        slide.dispatchEvent(
          new CustomEvent("slideshow:slide:personalisation:ready", {
            bubbles: true,
            detail: {
              slide,
              slideIndex,
              personalisationId: slide.dataset.personalisationId,
              contentId,
              decisionId,
              personalizationPointId,
              personalizationPointName,
              posture,
              attributes,
              personalization,
            },
          })
        );
      };

      resolveSdk()
        .then((SalesforceInteractions) => {
          console.log("[slideshow] Fetching personalisation for slides", pointNames);
          return SalesforceInteractions.Personalization.fetch(pointNames);
        })
        .then((response) => {
          clearTimeout(skeletonTimeout);
          const personalizations = (response && response.personalizations) || [];
          const byName = new Map();
          personalizations.forEach((p) => {
            if (p && p.personalizationPointName) byName.set(p.personalizationPointName, p);
          });

          slidesByPointName.forEach((slidesForName, name) => {
            const personalization = byName.get(name);
            if (!personalization) {
              console.warn(`[slideshow] No personalisation returned for "${name}"`);
              slidesForName.forEach((slide) => {
                const slideIndex = Array.from(slideshow.querySelectorAll(".carousel__slide")).indexOf(slide);
                const traceContext = {
                  personalisationId: name,
                  personalizationPointName: name,
                  posture: "no_match",
                  slideIndex,
                  slideId: slide.id || slide.dataset.blockId || undefined,
                };
                slideshowEmitTrace("decision", traceContext);
                slideshowEmitTrace("hidden_no_match", traceContext);
                clearSkeleton(slide);
              });
              return;
            }
            slidesForName.forEach((slide) => applyToSlide(slide, personalization));
          });
        })
        .catch((err) => {
          clearTimeout(skeletonTimeout);
          console.warn("[slideshow] Failed to fetch personalisations", pointNames, err);
          slides.forEach((slide) => {
            const slideIndex = Array.from(slideshow.querySelectorAll(".carousel__slide")).indexOf(slide);
            const traceContext = {
              personalisationId: slide.dataset.personalisationId,
              personalizationPointName: slide.dataset.personalisationId,
              posture: "no_match",
              slideIndex,
              slideId: slide.id || slide.dataset.blockId || undefined,
              source: "fetch_error",
            };
            slideshowEmitTrace("decision", traceContext);
            slideshowEmitTrace("hidden_no_match", traceContext);
            clearSkeleton(slide);
            slide.dispatchEvent(
              new CustomEvent("slideshow:slide:personalisation:error", {
                bubbles: true,
                detail: { slide, personalisationId: slide.dataset.personalisationId, error: err },
              })
            );
          });
        });
    }
    armSlideViewTrace(slide) {
      if (!slide || slide._slideshowViewArmed) return;
      slide._slideshowViewArmed = true;

      const fireView = (source) => {
        const ctx = slide._slideshowTraceContext;
        if (!ctx) return;
        slideshowEmitTrace("view", { ...ctx, source: source || "slide_visible" });
      };

      if (typeof IntersectionObserver === "function") {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              if (slide._slideshowViewFired) return;
              slide._slideshowViewFired = true;
              fireView("viewport");
            });
          },
          { threshold: 0.5 }
        );
        observer.observe(slide);
        slide._slideshowViewObserver = observer;
      } else {
        fireView("fallback");
        slide._slideshowViewFired = true;
      }

      if (!slide._slideshowSelectListener) {
        slide._slideshowSelectListener = () => {
          if (slide.classList.contains("is-selected")) {
            slide._slideshowViewFired = false;
            fireView("carousel_select");
            slide._slideshowViewFired = true;
          }
        };
        if (typeof MutationObserver === "function") {
          const mo = new MutationObserver(slide._slideshowSelectListener);
          mo.observe(slide, { attributes: true, attributeFilter: ["class"] });
          slide._slideshowSelectMutationObserver = mo;
        }
      }
    }
    armSlideClickTrace(slide) {
      if (!slide || slide._slideshowClickArmed) return;
      slide._slideshowClickArmed = true;
      slide.addEventListener("click", (e) => {
        const ctx = slide._slideshowTraceContext;
        if (!ctx) return;
        const clickEl = e.target && e.target.closest("a, button, [data-popup-cta], [role='button']");
        if (!clickEl || !slide.contains(clickEl)) return;
        const href = clickEl.getAttribute("href") || undefined;
        slideshowEmitTrace("click", {
          ...ctx,
          source: "slide_click",
          href,
        });
      });
    }
    applyPersonalisationContent(slide, attributes) {
      if (!slide || !attributes) return Promise.resolve();

      const revealPlaceholder = (el) => {
        if (!el) return;
        el.classList.remove("slideshow__personalisation-placeholder");
        if (el.parentElement) {
          el.parentElement.classList.remove("slideshow__personalisation-placeholder");
        }
      };

      if (attributes.Badge) {
        const badge = slide.querySelector(".inline-badge");
        if (badge) {
          badge.textContent = attributes.Badge;
          revealPlaceholder(badge);
        }
      }
      if (attributes.Subheading) {
        const subheading = slide.querySelector(".slideshow__slide-content .subheading");
        if (subheading) {
          subheading.textContent = attributes.Subheading;
          revealPlaceholder(subheading);
        }
      }
      if (attributes.Heading) {
        const heading = slide.querySelector(".slideshow__slide-heading");
        if (heading) {
          heading.innerHTML = String(attributes.Heading).replace(/\n/g, "<br>");
          revealPlaceholder(heading);
        }
      }
      if (attributes.Description) {
        const description = slide.querySelector(".slideshow__slide-content p.split-text:not(.subheading)");
        if (description) {
          description.innerHTML = String(attributes.Description).replace(/\n/g, "<br>");
          revealPlaceholder(description);
        }
      }
      if (attributes.Slide_Click_Link) {
        const links = slide.querySelectorAll("a:not(.button):not(.text-button)");
        links.forEach((a) => a.setAttribute("href", attributes.Slide_Click_Link));
      }
      if (attributes.Button_Label || attributes.Button_Link) {
        const buttonContainer = slide.querySelector(".button-overflow-container");
        if (buttonContainer) {
          const button = buttonContainer.querySelector(".button, .text-button");
          if (button) {
            if (attributes.Button_Label) {
              this.setButtonLabel(button, attributes.Button_Label);
            }
            if (attributes.Button_Link) {
              button.setAttribute("href", attributes.Button_Link);
            }
            revealPlaceholder(button);
          }
        }
      }
      if (attributes.Desktop_Video) this.replaceSlideVideo(slide, attributes.Desktop_Video, "desktop");
      if (attributes.Mobile_Video) this.replaceSlideVideo(slide, attributes.Mobile_Video, "mobile");

      const PRELOAD_TIMEOUT_MS = 4000;
      const preloads = [];
      if (attributes.Desktop_Image) {
        preloads.push(
          this.preloadImage(attributes.Desktop_Image, PRELOAD_TIMEOUT_MS).then(() =>
            this.replaceSlideImage(slide, attributes.Desktop_Image, "desktop")
          )
        );
      }
      if (attributes.Mobile_Image) {
        preloads.push(
          this.preloadImage(attributes.Mobile_Image, PRELOAD_TIMEOUT_MS).then(() =>
            this.replaceSlideImage(slide, attributes.Mobile_Image, "mobile")
          )
        );
      }

      return Promise.all(preloads).then(() => {
        slide.dispatchEvent(
          new CustomEvent("slideshow:slide:content:applied", {
            bubbles: true,
            detail: { slide, attributes },
          })
        );
      });
    }
    preloadImage(url, timeoutMs) {
      return new Promise((resolve) => {
        if (!url) return resolve();
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        const img = new Image();
        img.onload = done;
        img.onerror = done;
        img.src = url;
        if (img.complete) return done();
        if (timeoutMs) setTimeout(done, timeoutMs);
      });
    }
    replaceSlideImage(slide, url, variant) {
      if (!url) return;
      const containers = slide.querySelectorAll(".slideshow__slide-bg, .slideshow__slide-adapt-to-image");
      containers.forEach((container) => {
        let img;
        if (variant === "mobile") {
          img = container.querySelector(".slideshow-image--mobile");
        } else {
          img =
            container.querySelector(".slideshow-image--desktop") ||
            container.querySelector("img:not(.slideshow-image--mobile)");
        }
        if (!img) return;

        img.removeAttribute("srcset");
        img.removeAttribute("data-srcset");
        img.removeAttribute("data-sizes");
        if (img.hasAttribute("data-src")) img.setAttribute("data-src", url);
        img.setAttribute("src", url);
        img.classList.remove("lazyload", "blur");
        img.classList.add("lazyloaded", "no-blur");
      });
    }
    replaceSlideVideo(slide, url, variant) {
      if (!url) return;
      const containerSelector =
        variant === "mobile"
          ? ".slideshow__slide-video-bg.mobile-video-content"
          : ".slideshow__slide-video-bg.desktop-video-content";
      const container = slide.querySelector(containerSelector);
      if (!container) return;
      const video = container.querySelector("video");
      if (!video) return;

      video.querySelectorAll("source").forEach((s) => s.remove());
      video.setAttribute("src", url);
      try {
        video.load();
      } catch (e) {
        /* ignore */
      }
      if (video.muted || video.hasAttribute("autoplay")) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            /* autoplay blocked - ignore */
          });
        }
      }
    }
    setButtonLabel(button, label) {
      if (!button || !label) return;
      const icon = button.querySelector("span");
      Array.from(button.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) button.removeChild(node);
      });
      const textNode = document.createTextNode(label);
      if (icon) {
        button.insertBefore(textNode, icon);
      } else {
        button.appendChild(textNode);
      }
      button.setAttribute("title", label);
    }
    setupAutoplayProgress(slideshow, flkty) {
      const progressCircle = slideshow.querySelector(".thb-slideshow-progress--svg circle");
      const autoplayDuration = parseInt(slideshow.dataset.autoplay, 10);
      let animationFrame,
        startTime,
        pausedTime = 0;

      const circumference = progressCircle.getTotalLength();
      progressCircle.style.strokeDasharray = `${circumference} ${circumference}`;
      progressCircle.style.strokeDashoffset = circumference;

      function animate(timestamp) {
        if (!startTime) startTime = timestamp;
        const elapsedTime = timestamp - startTime + pausedTime;
        const progress = Math.min(elapsedTime / autoplayDuration, 1);

        const offset = circumference * (1 - progress);
        progressCircle.style.strokeDashoffset = offset;

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      }

      function resetAnimation() {
        cancelAnimationFrame(animationFrame);
        startTime = null;
        pausedTime = 0;
        progressCircle.style.strokeDashoffset = circumference;
      }

      function pauseAnimation() {
        cancelAnimationFrame(animationFrame);
        pausedTime += performance.now() - startTime;
      }

      function playAnimation() {
        startTime = null;
        animationFrame = requestAnimationFrame(animate);
      }

      // Bind slideshow events to control animation
      slideshow.addEventListener("mouseenter", () => {
        pauseAnimation();
        resetAnimation();
      });

      slideshow.addEventListener("mouseleave", () => {
        playAnimation();
      });

      // Bind Flickity change event to reset and play animation
      if (flkty) {
        flkty.on("change", () => {
          resetAnimation();
          playAnimation();
        });
      }

      // Start the animation initially
      playAnimation();
    }

    videoPause(video_container) {
      setTimeout(() => {
        video_container.querySelector("video").pause();
      }, 10);
    }
    videoPlay(video_container) {
      setTimeout(() => {
        video_container.querySelector("video").play();
      }, 10);
    }
    prepareAnimations(slideshow, animations) {
      if (!slideshow.dataset.animationsReady) {
        let splittext = new SplitText(slideshow.querySelectorAll(".slideshow__slide-heading, p:not(.subheading)"), {
          type: "lines",
          linesClass: "line-child",
        });
        let mask = new SplitText(slideshow.querySelectorAll(".slideshow__slide-heading, p:not(.subheading)"), {
          type: "lines",
          linesClass: "line-parent",
        });

        slideshow.querySelectorAll(".slideshow__slide").forEach((item, i) => {
          let tl = gsap.timeline({
              paused: true,
            }),
            button_offset = 0;

          animations[i] = tl;

          if (slideshow.dataset.transition == "zoom") {
            tl.to(
              item.querySelectorAll(".slideshow__slide-bg, .slideshow__slide-video-bg"),
              {
                duration: 1.5,
                scale: 1,
              },
              "start"
            );
          }

          if (item.querySelector(".inline-badge")) {
            tl.fromTo(
              item.querySelector(".inline-badge"),
              {
                opacity: 0,
              },
              {
                duration: 0.5,
                opacity: 1,
              },
              0
            );

            button_offset += 0.2;
          }
          if (item.querySelector(".subheading")) {
            tl.fromTo(
              item.querySelector(".subheading"),
              {
                opacity: 0,
              },
              {
                duration: 0.5,
                opacity: 1,
              },
              0
            );

            button_offset += 0.5;
          }
          if (item.querySelector(".slideshow__slide-heading")) {
            let h1_duration = 1 + (item.querySelectorAll(".slideshow__slide-heading .line-child").length - 1) * 0.1;
            tl.set(
              item.querySelectorAll(".slideshow__slide-heading"),
              {
                opacity: 1,
              },
              0
            ).from(
              item.querySelectorAll(".slideshow__slide-heading .line-child"),
              {
                duration: h1_duration,
                yPercent: 120,
                stagger: 0.1,
                rotation: "3deg",
              },
              0
            );
            button_offset += h1_duration;
          }
          if (item.querySelector("p.split-text")) {
            let p_duration = 1 + (item.querySelectorAll("p.split-text .line-child").length - 1) * 0.05;
            tl.set(
              item.querySelectorAll("p.split-text"),
              {
                opacity: 1,
              },
              0
            ).from(
              item.querySelectorAll("p:not(.subheading) .line-child"),
              {
                duration: p_duration,
                yPercent: "120",
                stagger: 0.1,
                rotation: "3deg",
              },
              0
            );
            button_offset += p_duration;
          }
          if (item.querySelectorAll(".button, .text-button")) {
            tl.fromTo(
              item.querySelectorAll(".button, .text-button"),
              {
                y: "100%",
              },
              {
                duration: 0.5,
                y: "0%",
                stagger: 0.1,
              },
              button_offset * 0.2
            );
          }
          item.dataset.timeline = tl;
        });

        slideshow.dataset.animationsReady = true;
      }
    }
    animateSlides(i, slideshow, animations) {
      let flkty = Flickity.data(slideshow),
        active_slide = flkty.selectedElement;
      document.fonts.ready.then(function () {
        animations[i].restart();
      });
    }
    animateReverse(i, slideshow, animations) {
      animations[i].reverse();
    }
    centerArrows(flickity, slideshow, prev_button, next_button) {
      let first_cell = flickity.cells[0],
        max_height = 0,
        image_height = 0;
      if (first_cell.element.querySelector(".product-featured-image")) {
        image_height = first_cell.element.querySelector(".product-featured-image").clientHeight;
      } else if (first_cell.element.querySelector(".gallery--item")) {
        image_height = flickity.cells[1].element.querySelector(".product-featured-image").clientHeight;
      }
      if (slideshow.classList.contains("customer-reviews--carousel")) {
        if (first_cell.element.querySelector(".customer-reviews--product")) {
          image_height = flickity.cells[1].element.querySelector(".customer-reviews--product").clientHeight;
        }
      }
      if (image_height > 0) {
        flickity.cells.forEach((item, i) => {
          if (item.size.height > max_height) {
            max_height = item.size.height;
          }
        });

        if (max_height > image_height) {
          let difference = (max_height - image_height) / -2;

          prev_button.style.transform = "translateY(" + difference + "px)";
          next_button.style.transform = "translateY(" + difference + "px)";
        }
      }
    }
  }
  customElements.define("slide-show", SlideShow);
}
